import { useCallback, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { setShowHoldAndWinOutro, setShowMaxWin, setShowHoldAndWinCollector } from "../State/actions";
import store from "../State/Store/store";
import {
  selectRespinsLeft,
  selectFeatureSpinComplete,
  selectHoldAndWinCurrentRound,
  selectBet,
  selectHoldAndWinActive,
  selectFeatureSpinActive,
  selectIsHoldAndWinIntroReload,
  selectIsHoldAndWinMidFeatureReload,
  selectIsHoldAndWinCompletionReload,
} from "../State/selectors";
import { BLANK_SYMBOL_ID, COIN_SYMBOL_IDS, symbolData } from "../SymbolData";
import { GAME_LAYOUT } from "../settings";
import { FEATURES_CONFIG } from "../featuresConfig";

/**
 * Hook for orchestrating Hold and Win collection phase
 * Manages symbol-by-symbol collection and collector display
 */
export const useHoldAndWinCollectionController = () => {
  const dispatch = useDispatch();
  const {
    holdAndWinReelRefs,
    holdAndWinOverlayReelRefs,
    holdAndWinCollectorDisplayRef,
    holdAndWinCoinCollectRef,
    holdAndWinCounterRef,
    holdAndWinProgressBarRef,
    gameContainerRef,
    playButtonRef,
    reelFrameDecorationRef,
  } = useReelRefs();

  const respinsLeft = useSelector(selectRespinsLeft);
  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const bet = useSelector(selectBet);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  // autoPlaySpeed removed - read directly from store to prevent timeout resets on speed changes
  const isHoldAndWinIntroReload = useSelector(selectIsHoldAndWinIntroReload);
  const isHoldAndWinMidFeatureReload = useSelector(selectIsHoldAndWinMidFeatureReload);
  const isHoldAndWinCompletionReload = useSelector(selectIsHoldAndWinCompletionReload);

  // Memoize constants (static values)
  const reelCount = useMemo(() => GAME_LAYOUT.REEL_COUNT, []);
  const symbolsVisible = useMemo(() => GAME_LAYOUT.SYMBOLS_VISIBLE, []);

  // Memoize maxWinThreshold calculation
  const maxWinThreshold = useMemo(
    () => bet * FEATURES_CONFIG.HOLD_AND_WIN.MAX_WIN_MULTIPLIER,
    [bet]
  );

  const isCollectingRef = useRef(false);
  const currentTimeoutsRef = useRef([]);
  const collectionTriggeredRef = useRef(false);
  const skipTriggeredRef = useRef(false);
  const finalTotalDisplayValueRef = useRef(0); // Store the final total before multiplier
  const maxWinTriggeredRef = useRef(false); // Track if max win was triggered
  const isProcessingSkipRef = useRef(false);
  const isInReloadStateRef = useRef(false);

  /**
   * Clear all timeouts
   */
  const clearAllTimeouts = useCallback(() => {
    currentTimeoutsRef.current.forEach(clearTimeout);
    currentTimeoutsRef.current = [];
  }, []);

  /**
   * Collect symbol data from all reels
   */
  const collectSymbolData = useCallback(() => {
    const symbolsToCollect = [];
    const reels = holdAndWinReelRefs.current;
    const currentRound = holdAndWinCurrentRound;

    if (!reels || !Array.isArray(reels) || !currentRound?.reels) {
      return symbolsToCollect;
    }

    for (let reelIndex = 0; reelIndex < reelCount; reelIndex++) {
      const reel = reels[reelIndex];
      if (!reel) continue;

      const reelData = currentRound.reels[reelIndex];
      if (!Array.isArray(reelData)) continue;

      for (
        let symbolIndex = 0;
        symbolIndex < symbolsVisible;
        symbolIndex++
      ) {
        const symbolId = reelData[symbolIndex];

        if (
          symbolId === BLANK_SYMBOL_ID ||
          !COIN_SYMBOL_IDS.includes(symbolId)
        ) {
          continue;
        }

        const symbolInfo = symbolData[symbolId];
        if (!symbolInfo || !symbolInfo.value) {
          continue;
        }

        const coinValue = parseInt(symbolInfo.value, 10);
        if (isNaN(coinValue) || coinValue <= 0) {
          continue;
        }

        const multiplierRaw =
          currentRound.storage?.coin_multipliers?.[reelIndex]?.[symbolIndex];
        const multiplier =
          multiplierRaw !== undefined && multiplierRaw !== null
            ? Number(multiplierRaw)
            : 1;

        const coinValueWithMultiplier = coinValue * multiplier;

        symbolsToCollect.push({
          reelIndex,
          symbolIndex,
          symbolId,
          coinValue,
          multiplier,
          value: coinValueWithMultiplier,
        });
      }
    }

    return symbolsToCollect;
  }, [holdAndWinReelRefs, holdAndWinCurrentRound, reelCount, symbolsVisible]);

  /**
   * Finish collection and trigger outro
   * Checks for progress bar multiplier (x2 at 15 coins, x5 at 20 coins)
   * and plays multiplier animation before hiding collector
   * Also checks for max win threshold (bet * 5000)
   */
  const finishCollection = useCallback(() => {
    // Guard: If collection was already stopped (e.g., by max win), don't proceed
    if (!isCollectingRef.current || maxWinTriggeredRef.current) {
      return;
    }

    isCollectingRef.current = false;

    const collectorDisplay = holdAndWinCollectorDisplayRef.current;

    // Get progress bar value to determine multiplier
    const progressBarValue = holdAndWinCurrentRound?.storage?.progress_bar || 0;

    // Determine multiplier based on progress bar value
    // 20 coins = x5 multiplier, 15-19 coins = x2 multiplier
    let multiplierValue = 1;
    if (progressBarValue === 20) {
      multiplierValue = 5;
    } else if (progressBarValue >= 15 && progressBarValue < 20) {
      multiplierValue = 2;
    }

    const coinCollectComponent = holdAndWinCoinCollectRef.current;

    // Disable skip mode when finishing collection
    if (collectorDisplay?.disableSkipMode) {
      collectorDisplay.disableSkipMode();
    }
    const totalWinFromAPI = holdAndWinCurrentRound?.win || 0;


    // If we have a multiplier (x2 or x5), play the multiplier animation
    if (
      multiplierValue > 1 &&
      coinCollectComponent?.playProgressMultiplierAnimation
    ) {
      // CRITICAL: Stop the counter animation and sound FIRST before applying multiplier
      if (collectorDisplay?.stopCounterAnimation) {
        collectorDisplay.stopCounterAnimation();
      }

      // Then set counter to final value before multiplier
      if (collectorDisplay?.getController) {
        const controller = collectorDisplay.getController();
        if (controller) {
          // Jump counter to final value immediately
          controller.currentValue = finalTotalDisplayValueRef.current;
          controller.updateCollectorDisplay(finalTotalDisplayValueRef.current);
        }
      }

      // Play multiplier animation
      coinCollectComponent
        .playProgressMultiplierAnimation(multiplierValue)
        .then(() => {
          // Guard: check if still collecting
          if (!isCollectingRef.current && !collectorDisplay) return;

          // After animation completes, multiply the collector value
          if (collectorDisplay) {
            // CRITICAL FIX: Use the stored final total value, NOT the animated counter value
            // The counter might still be animating when we reach this point
            const finalTotalBeforeMultiplier = finalTotalDisplayValueRef.current;
            const multipliedValue = finalTotalBeforeMultiplier * multiplierValue;

            // Cap the multiplied value to the actual win from API (server-side max win cap)
            // This ensures collector shows the same value as outro dialog when max win is hit
            const cappedValue = Math.min(multipliedValue, totalWinFromAPI);

            // Start new counter animation for multiplier
            collectorDisplay.startCounterAnimation(
              cappedValue,
              FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.COUNTER_ANIMATION_MULTIPLIER,
              () => {
                // Counter animation complete - stop sound immediately
                if (collectorDisplay?.stopCounterAnimation) {
                  collectorDisplay.stopCounterAnimation();
                }

                if (!isCollectingRef.current && !collectorDisplay) return;

                // Wait a moment to show the multiplied value, then hide collector
                const hideTimeout = setTimeout(() => {
                  collectorDisplay.hideCollector();

                  // FINAL CHECK: Verify total win from API against max win threshold
                  // This is a safety check in case collector display doesn't match the actual win
                  if (totalWinFromAPI >= maxWinThreshold) {
                    // MAX WIN REACHED based on API total! Show max win dialog instead of outro
                    const maxWinTimeout = setTimeout(() => {
                      dispatch(setShowMaxWin(true));
                      collectionTriggeredRef.current = false;
                    }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.OUTRO_DELAY);

                    currentTimeoutsRef.current.push(maxWinTimeout);
                    return;
                  }

                  // Trigger Hold and Win outro after delay
                  const outroTimeout = setTimeout(() => {
                    dispatch(setShowHoldAndWinOutro(true));
                    collectionTriggeredRef.current = false;
                  }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.OUTRO_DELAY);

                  currentTimeoutsRef.current.push(outroTimeout);
                }, 1000); // Show multiplied value for 1 second

                currentTimeoutsRef.current.push(hideTimeout);
              }
            );
            // Note: Max win check happens inside startCounterAnimation via callback
          }
        });
    } else {
      // No multiplier - proceed normally
      // CRITICAL FIX: Wait for counter animation to complete before hiding collector
      // Check if counter is still animating
      if (collectorDisplay?.isCounterAnimating()) {
        // Wait for counter to finish, then hide
        const checkInterval = setInterval(() => {
          if (!collectorDisplay.isCounterAnimating()) {
            clearInterval(checkInterval);
            collectorDisplay.hideCollector();

            // Proceed with outro after hiding
            proceedToOutro();
          }
        }, 100);
        
        // CRITICAL: Track interval for cleanup to prevent memory leak
        currentTimeoutsRef.current.push(checkInterval);
        return;
      }

      if (collectorDisplay) {
        collectorDisplay.hideCollector();
      }

      proceedToOutro();
    }

    function proceedToOutro() {
      // FINAL CHECK: Verify total win from API against max win threshold
      // This is a safety check in case collector display doesn't match the actual win
      if (totalWinFromAPI >= maxWinThreshold) {
        // MAX WIN REACHED based on API total! Show max win dialog instead of outro
        const maxWinTimeout = setTimeout(() => {
          dispatch(setShowMaxWin(true));
          collectionTriggeredRef.current = false;
        }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.OUTRO_DELAY);

        currentTimeoutsRef.current.push(maxWinTimeout);
        return;
      }

      // Trigger Hold and Win outro after delay
      const outroTimeout = setTimeout(() => {
        dispatch(setShowHoldAndWinOutro(true));
        collectionTriggeredRef.current = false;
      }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.OUTRO_DELAY);

      currentTimeoutsRef.current.push(outroTimeout);
    }
  }, [
    dispatch,
    holdAndWinCollectorDisplayRef,
    holdAndWinCoinCollectRef,
    holdAndWinCurrentRound,
    maxWinThreshold,
  ]);

  /**
   * Play win animation once for all coins before collection starts
   */
  const playWinAnimationsForAllCoins = useCallback(() => {
    const symbolsToCollect = collectSymbolData();

    symbolsToCollect.forEach((symbol) => {
      const overlayReel = holdAndWinOverlayReelRefs.current[symbol.reelIndex];
      const overlaySymbolRef = overlayReel?.getSymbolRef?.(symbol.symbolIndex);

      if (overlaySymbolRef?.showWinAnimation) {
        // Play win animation once (non-looping, no text)
        overlaySymbolRef.showWinAnimation(symbol.symbolId, false, false);
      }
    });
  }, [holdAndWinOverlayReelRefs, collectSymbolData]);

  /**
   * Trigger skip for collection - speeds up remaining coins
   */
  const triggerCollectionSkip = useCallback(() => {
    // Only allow skip during active collection
    if (!isCollectingRef.current) {
      return;
    }

    // Prevent double-skip
    if (skipTriggeredRef.current || isProcessingSkipRef.current) {
      return;
    }

    skipTriggeredRef.current = true;
  }, []);

  /**
   * Start collection sequence
   */
  const startCollection = useCallback(() => {
    if (isCollectingRef.current) {
      return;
    }


    // CRITICAL: Set this flag here (not before timeout) to prevent race conditions
    // If set too early, collection won't retrigger when spin completes
    collectionTriggeredRef.current = true;
    isCollectingRef.current = true;
    skipTriggeredRef.current = false;
    isProcessingSkipRef.current = false;
    maxWinTriggeredRef.current = false; // Reset max win flag for new collection
    clearAllTimeouts();

    // Stop tension effect animation if it was active during any round
    // Pass forceStop=true to override the holdAndWinActive guard
    gameContainerRef?.current?.stopTensionEffect?.(true);

    // Stop progress bar tension animation and sound (if active at 14 or 19 coins)
    holdAndWinProgressBarRef?.current?.stopTension?.();

    // STEP 1: Hide counter immediately via controller (preserves animation state)
    holdAndWinCounterRef?.current?.hide();
    // Hide respins display at the same time as counter
    dispatch(setShowHoldAndWinCollector(true));

    // STEP 2: Play win animations for all coins
    playWinAnimationsForAllCoins();

    // STEP 3: Play end animation (glow effect) - now handled by ReelFrameDecoration
    const reelFrameDecoration = reelFrameDecorationRef.current;
    if (reelFrameDecoration?.playEndAnimation) {
      reelFrameDecoration.playEndAnimation().then(() => {
        // Guard: check if still collecting after end animation
        if (!isCollectingRef.current) return;

        // STEP 4: Show collector with intro animation (AFTER end animation completes)
        const collectorDisplay = holdAndWinCollectorDisplayRef.current;
        if (!collectorDisplay) {
          isCollectingRef.current = false;
          return;
        }

      collectorDisplay.showCollector();

      // Setup max win check for counter animation
      collectorDisplay.setMaxWinCheck(maxWinThreshold, (reachedValue) => {
          // MAX WIN REACHED during counter animation!
          isCollectingRef.current = false;
          maxWinTriggeredRef.current = true; // Mark that max win was triggered

          const hideTimeout = setTimeout(() => {
            collectorDisplay.hideCollector();

            const maxWinTimeout = setTimeout(() => {
              dispatch(setShowMaxWin(true));
              collectionTriggeredRef.current = false;
            }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.OUTRO_DELAY);

            currentTimeoutsRef.current.push(maxWinTimeout);
          }, 1000); // Show max win value for 1 second

          currentTimeoutsRef.current.push(hideTimeout);
        });

        // Wait for collector intro animation
        const introTimeout = setTimeout(() => {
          // Guard: check if still collecting before proceeding
          if (!isCollectingRef.current) return;

          // STEP 5: Start collecting symbols
          startSymbolCollection();
        }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.INTRO_ANIMATION_DELAY);

        currentTimeoutsRef.current.push(introTimeout);
      });
    } else {
      // Fallback: show collector immediately if end animation not available
      const collectorDisplay = holdAndWinCollectorDisplayRef.current;
      if (!collectorDisplay) {
        isCollectingRef.current = false;
        return;
      }

      collectorDisplay.showCollector();

      // Setup max win check for counter animation
      collectorDisplay.setMaxWinCheck(maxWinThreshold, (reachedValue) => {
        // MAX WIN REACHED during counter animation!
        isCollectingRef.current = false;
        maxWinTriggeredRef.current = true; // Mark that max win was triggered

        const hideTimeout = setTimeout(() => {
          collectorDisplay.hideCollector();

          const maxWinTimeout = setTimeout(() => {
            dispatch(setShowMaxWin(true));
            collectionTriggeredRef.current = false;
          }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.OUTRO_DELAY);

          currentTimeoutsRef.current.push(maxWinTimeout);
        }, 1000); // Show max win value for 1 second

        currentTimeoutsRef.current.push(hideTimeout);
      });

      // Wait for collector intro animation
      const introTimeout = setTimeout(() => {
        // Guard: check if still collecting before proceeding
        if (!isCollectingRef.current) return;

        startSymbolCollection();
      }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.INTRO_ANIMATION_DELAY);

      currentTimeoutsRef.current.push(introTimeout);
    }

    // Extract collection logic into a function
    function startSymbolCollection() {
      // Collect symbols
      const symbolsToCollect = collectSymbolData();

      if (symbolsToCollect.length === 0) {
        finishCollection();
        return;
      }

      // Get collector display controller
      const collectorDisplay = holdAndWinCollectorDisplayRef.current;
      if (!collectorDisplay) {
        isCollectingRef.current = false;
        return;
      }

      // Calculate total final value for continuous counter animation
      const totalValue = symbolsToCollect.reduce((sum, symbol) => sum + symbol.value, 0);
      const totalDisplayValue = totalValue * (Number(bet) || 0);


      // CRITICAL FIX: Store the final total in a ref so we can use it for multiplier calculation
      // This ensures we multiply the correct final value, not the animated counter value
      finalTotalDisplayValueRef.current = totalDisplayValue;

      // Calculate counter animation duration based on actual coin collection timing
      // CRITICAL BUG FIX: Calculate time for EACH coin individually based on whether it has a multiplier
      // Coins collect sequentially: next coin starts when previous coin's particle arrives
      
      // Get multiplier animation duration from spine data (not hardcoded)
      // Find a coin that actually has a multiplier to read the spine duration
      let multiplierAnimTime = 1050; // Fallback: 500ms out + 500ms counter + 50ms delay
      let foundSpineDuration = false;
      
      for (const coin of symbolsToCollect) {
        if (coin.multiplier > 1) {
          const overlayReel = holdAndWinOverlayReelRefs.current[coin.reelIndex];
          const overlaySymbol = overlayReel?.getSymbolRef?.(coin.symbolIndex);
          
          if (overlaySymbol?.getTotalMultiplierAnimationTime) {
            multiplierAnimTime = overlaySymbol.getTotalMultiplierAnimationTime();
            foundSpineDuration = true;
            break;
          }
        }
      }
      
      if (!foundSpineDuration) {
        // Using fallback duration: 1050ms
      }
      
      // Calculate time for each coin: SYMBOL_COLLECT_DELAY + (multiplierAnimTime if multiplier > 1)
      let actualCollectionTime = 0;
      
      symbolsToCollect.forEach((coin, idx) => {
        const baseTime = FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.SYMBOL_COLLECT_DELAY;
        const hasMultiplier = coin.multiplier > 1;
        const coinTime = baseTime + (hasMultiplier ? multiplierAnimTime : 0);
        
        actualCollectionTime += coinTime;
      });
      
      const counterAnimationDuration = actualCollectionTime;

      // Iterate through symbols one by one
      let runningTotal = 0;
      let symbolIndex = 0;
      let hasStartedCounter = false;
      const collectedCoins = new Set(); // Track which coins have been collected

      /**
       * Process all remaining coins at once (skip mode)
       * Applies multipliers in parallel, then collects all coins simultaneously
       */
      const processRemainingCoinsAtOnce = async () => {
        // Enable skip mode on collector to play sound only once
        if (collectorDisplay?.enableSkipMode) {
          collectorDisplay.enableSkipMode();
        }

        // Get all remaining coins
        const remainingCoins = symbolsToCollect.slice(symbolIndex);

        if (remainingCoins.length === 0) {
          finishCollection();
          return;
        }


        // Step 1: Apply all multipliers in parallel
        const multiplierPromises = [];
        const coinsWithMultipliers = [];

        for (const coin of remainingCoins) {
          if (coin.multiplier > 1) {
            coinsWithMultipliers.push(`[${coin.reelIndex},${coin.symbolIndex}]`);
            const overlayReel = holdAndWinOverlayReelRefs.current[coin.reelIndex];
            const overlaySymbolRef = overlayReel?.getSymbolRef?.(coin.symbolIndex);

            if (overlaySymbolRef?.applyMultiplierAndAnimate) {
              const promise = overlaySymbolRef.applyMultiplierAndAnimate(coin.multiplier);
              if (promise && promise.then) {
                multiplierPromises.push(
                  promise.catch(() => {
                    return Promise.resolve();
                  })
                );
              }
            }
          }
        }

        // Wait for all multiplier animations to complete (with error handling)
        if (multiplierPromises.length > 0) {
          try {
            await Promise.allSettled(multiplierPromises);
          } catch (error) {
            // Continue even if some animations fail
          }
        }

        // Guard: check if still collecting after multipliers
        if (!isCollectingRef.current) {
          return;
        }

        // Step 2: Calculate final total for all remaining coins
        let skipTotal = runningTotal;
        remainingCoins.forEach((coin) => {
          skipTotal += coin.value;
        });
        const finalDisplayValue = skipTotal * (Number(bet) || 0);


        // Step 3: Start counter animation immediately for skip mode
        let counterComplete = false;
        let particlesComplete = false;

        const checkAndFinish = () => {
          if (counterComplete && particlesComplete) {
            finishCollection();
          }
        };

        // CRITICAL: Stop any existing counter animation before starting skip mode
        if (collectorDisplay.stopCounterAnimation) {
          collectorDisplay.stopCounterAnimation();
        }

        collectorDisplay.startCounterAnimation(
          finalDisplayValue,
          FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.COUNTER_ANIMATION_SKIP_MODE,
          () => {
            counterComplete = true;
            checkAndFinish();
          }
        );

        // Step 4: Process all remaining coins simultaneously (visual effects)
        // Collect all particle animation promises
        const particlePromises = [];
        const particleTracker = []; // Track which coins are being collected
        const skippedCoinsSet = new Set(); // Track collected coins in skip mode

        remainingCoins.forEach((coin) => {
          const coinId = `[${coin.reelIndex},${coin.symbolIndex}]`;
          const coinKey = `${coin.reelIndex}-${coin.symbolIndex}`;
          particleTracker.push(coinId);
          skippedCoinsSet.add(coinKey);
          
          // Get overlay symbol ref
          const overlayReel = holdAndWinOverlayReelRefs.current[coin.reelIndex];
          const overlaySymbolRef = overlayReel?.getSymbolRef?.(coin.symbolIndex);

          // Fade out value text
          if (overlaySymbolRef?.fadeCoinValueText) {
            overlaySymbolRef.fadeCoinValueText();
          }

          // Play dark animation
          if (overlaySymbolRef?.showDarkAnimation) {
            overlaySymbolRef.showDarkAnimation(coin.symbolId);
          }

          // Play coin collection particle animation
          const coinCollectComponent = holdAndWinCoinCollectRef.current;
          if (coinCollectComponent?.playCollectionAnimation) {
            const displayValue = coin.value * (Number(bet) || 1);

            const particlePromise = coinCollectComponent
              .playCollectionAnimation(
                coin.reelIndex,
                coin.symbolIndex,
                displayValue
              )
              .then(() => {
                collectedCoins.add(coinKey); // Mark as collected
              })
              .catch((error) => {
                // CRITICAL: Still mark as collected even if animation failed
                collectedCoins.add(coinKey);
                return Promise.resolve();
              });

            particlePromises.push(particlePromise);
          }
        });


        // Step 5: Wait for all particles to arrive
        Promise.allSettled(particlePromises).then((results) => {
          // Guard: check if still collecting
          if (!isCollectingRef.current) {
            return;
          }


          // CRITICAL: Verify all coins were collected
          const missedCoins = [];
          skippedCoinsSet.forEach(coinKey => {
            if (!collectedCoins.has(coinKey)) {
              missedCoins.push(coinKey);
            }
          });

          // Note: Verification complete - all coins should be collected

          // Play hit animation once for all coins in skip mode
          // (skip mode already plays sound only once via enableSkipMode)
          const controller = collectorDisplay.getController?.();
          if (controller?.playHitAnimation) {
            controller.playHitAnimation();
          }

          // Update running total to match final value
          runningTotal = skipTotal;
          particlesComplete = true;
          checkAndFinish();
        });
        // Note: Max win check happens inside startCounterAnimation via callback
      };

      const collectNextSymbol = () => {
        // Guard: check if still collecting before proceeding
        if (!isCollectingRef.current) {
          return;
        }

        // Guard: if skip is already being processed, prevent any further normal collection
        if (isProcessingSkipRef.current) {
          return;
        }

        // Check if skip was triggered - process all remaining coins at once
        if (skipTriggeredRef.current && !isProcessingSkipRef.current) {
          isProcessingSkipRef.current = true;
          processRemainingCoinsAtOnce();
          return;
        }

        if (symbolIndex >= symbolsToCollect.length) {
          // All symbols collected - counter animation will call finishCollection when done
          return;
        }

        const symbol = symbolsToCollect[symbolIndex];
        symbolIndex++;

        // Get symbol controller
        const reel = holdAndWinReelRefs.current[symbol.reelIndex];
        const symbolRef = reel?.getSymbolRef?.(symbol.symbolIndex);
        const symbolController = symbolRef?.current?.getSymbolController?.();

        if (!symbolController) {
          const nextTimeout = setTimeout(() => {
            // Guard: check if still collecting before executing
            if (!isCollectingRef.current) return;
            collectNextSymbol();
          }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.SYMBOL_COLLECT_DELAY);
          currentTimeoutsRef.current.push(nextTimeout);
          return;
        }

        // Handle multiplier if present
        if (symbol.multiplier > 1) {
          // Get overlay symbol ref (where multiplier spine lives)
          const overlayReel =
            holdAndWinOverlayReelRefs.current[symbol.reelIndex];
          const overlaySymbolRef = overlayReel?.getSymbolRef?.(
            symbol.symbolIndex
          );

          // Play multiplier out animation, then multiply text
          const animatePromise = overlaySymbolRef?.applyMultiplierAndAnimate?.(
            symbol.multiplier
          );

          if (animatePromise && animatePromise.then) {
            animatePromise.then(() => {
              // Guard: check if still collecting before proceeding
              if (!isCollectingRef.current) {
                return;
              }

              // After multiplier animation completes and text is updated, wait a moment so user can see the multiplied value
              const delayTimeout = setTimeout(() => {
                // Guard: check again before executing
                if (!isCollectingRef.current) {
                  return;
                }
                fadeOutAndCollect();
              }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.MULTIPLIER_UPDATE_DELAY);
              currentTimeoutsRef.current.push(delayTimeout);
            }).catch((error) => {
              // CRITICAL FIX: Still proceed with collection even if multiplier fails
              if (!isCollectingRef.current) return;
              fadeOutAndCollect();
            });
          } else {
            // Fallback if overlay ref not available
            const delayTimeout = setTimeout(() => {
              // Guard: check if still collecting before executing
              if (!isCollectingRef.current) return;
              fadeOutAndCollect();
            }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.MULTIPLIER_UPDATE_DELAY);
            currentTimeoutsRef.current.push(delayTimeout);
          }
        } else {
          fadeOutAndCollect();
        }

        function fadeOutAndCollect() {
        // Guard: check if still collecting before proceeding
        if (!isCollectingRef.current) {
          return;
        }

          // Get overlay symbol ref (where coin value text lives)
          const overlayReel =
            holdAndWinOverlayReelRefs.current[symbol.reelIndex];
          const overlaySymbolRef = overlayReel?.getSymbolRef?.(
            symbol.symbolIndex
          );

          // IMMEDIATELY fade out value text from coin spine (via overlay controller)
          // This happens right when animation starts, not when it finishes
          if (overlaySymbolRef?.fadeCoinValueText) {
            overlaySymbolRef.fadeCoinValueText();
          }

          // Play dark animation on the coin to indicate it's been collected
          if (overlaySymbolRef?.showDarkAnimation) {
            overlaySymbolRef.showDarkAnimation(symbol.symbolId);
          }

          // Play coin collection particle animation
          const coinCollectComponent = holdAndWinCoinCollectRef.current;
          if (coinCollectComponent?.playCollectionAnimation) {
            // Calculate display value (coinValue * bet) and format it
            const displayValue = symbol.value * (Number(bet) || 1);

            // Start particle animation first
            coinCollectComponent
              .playCollectionAnimation(
                symbol.reelIndex,
                symbol.symbolIndex,
                displayValue
              )
              .then(() => {
                // Particle arrived!
                if (!isCollectingRef.current) {
                  return;
                }

                // Mark coin as collected
                const coinKey = `${symbol.reelIndex}-${symbol.symbolIndex}`;
                collectedCoins.add(coinKey);

                // Start continuous counter animation on first coin only
                if (!hasStartedCounter) {
                  hasStartedCounter = true;
                  collectorDisplay.startCounterAnimation(
                    totalDisplayValue,
                    counterAnimationDuration,
                    () => {
                      // Counter animation completed - now finish collection
                      // Note: Verification complete - all coins should be collected
                      finishCollection();
                    }
                  );
                }

                // CRITICAL: Play hit animation when each particle arrives
                // This provides visual feedback for each coin collected
                const controller = collectorDisplay.getController?.();
                if (controller?.playHitAnimation) {
                  controller.playHitAnimation();
                }

                // Add to running total (for tracking, counter animates independently)
                runningTotal += symbol.value;

                // Start next coin immediately
                collectNextSymbol();
              })
              .catch((error) => {
                // CRITICAL FIX: Still proceed to next coin even if particle fails
                if (!isCollectingRef.current) return;
                
                // Mark coin as collected even if animation failed
                const coinKey = `${symbol.reelIndex}-${symbol.symbolIndex}`;
                collectedCoins.add(coinKey);
                
                // Add to running total anyway
                runningTotal += symbol.value;
                
                // Start counter if not started
                if (!hasStartedCounter) {
                  hasStartedCounter = true;
                  collectorDisplay.startCounterAnimation(
                    totalDisplayValue,
                    counterAnimationDuration,
                    () => {
                      // Note: Verification complete - all coins should be collected
                      finishCollection();
                    }
                  );
                }
                
                // Continue to next coin
                collectNextSymbol();
              });
            // Note: Max win check happens inside startCounterAnimation via callback
          } else {
            // Fallback if coin collect component not available
            // Add to running total
            runningTotal += symbol.value;

            // Play hit animation for visual feedback
            const controller = collectorDisplay.getController?.();
            if (controller?.playHitAnimation) {
              controller.playHitAnimation();
            }

            // Animate collector display from current to new value (500ms per coin)
            const displayValue = runningTotal * (Number(bet) || 0);
            collectorDisplay.startCounterAnimation(
              displayValue,
              500, // 500ms per coin
              () => {
                // Counter animation complete, move to next symbol
                // Guard: check if still collecting
                if (!isCollectingRef.current) return;
                collectNextSymbol();
              }
            );
            // Note: Max win check happens inside startCounterAnimation via callback
          }
        }
      };

      // Check if turbo mode is active - auto-trigger skip for fast collection
      // Read speed directly from store to avoid effect re-runs on speed changes
      const isTurboMode = store.getState().settings.autoPlaySpeed === "turbo";
      if (isTurboMode) {
        skipTriggeredRef.current = true;
      }

      // Start collecting
      const startTimeout = setTimeout(() => {
        // Guard: check if still collecting before executing
        if (!isCollectingRef.current) {
          return;
        }
        collectNextSymbol();
      }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.SYMBOL_COLLECT_DELAY);
      currentTimeoutsRef.current.push(startTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    holdAndWinCollectorDisplayRef,
    holdAndWinCoinCollectRef,
    holdAndWinReelRefs,
    holdAndWinOverlayReelRefs,
    collectSymbolData,
    bet,
    clearAllTimeouts,
    finishCollection,
    playWinAnimationsForAllCoins,
    gameContainerRef,
    holdAndWinCounterRef,
    // holdAndWinProgressBarRef is a ref (stable), no need to include
    reelFrameDecorationRef,
    // autoPlaySpeed removed - read directly from store to prevent timeout resets on speed changes
    dispatch,
  ]);

  /**
   * Main effect: Trigger collection when conditions are met
   */
  useEffect(() => {
    const shouldStartCollection =
      holdAndWinActive &&
      respinsLeft === 0 &&
      featureSpinComplete &&
      !featureSpinActive &&
      !isCollectingRef.current &&
      !collectionTriggeredRef.current &&
      !isInReloadStateRef.current && // Don't trigger during reload state
      !isHoldAndWinCompletionReload; // Don't trigger for completion reload - show dialog immediately

    if (shouldStartCollection) {
      // CRITICAL: Don't set collectionTriggeredRef here - set it in startCollection()
      // This allows the effect to retrigger if conditions change before timeout fires

      // Small delay to ensure everything is settled
      const triggerTimeout = setTimeout(() => {
        startCollection();
      }, FEATURES_CONFIG.HOLD_AND_WIN.COLLECTION.TRIGGER_DELAY);

      currentTimeoutsRef.current.push(triggerTimeout);

      // Cleanup: clear timeout if effect re-runs or component unmounts
      return () => {
        clearTimeout(triggerTimeout);
        const index = currentTimeoutsRef.current.indexOf(triggerTimeout);
        if (index > -1) {
          currentTimeoutsRef.current.splice(index, 1);
        }
      };
    }
  }, [
    holdAndWinActive,
    respinsLeft,
    featureSpinComplete,
    featureSpinActive,
    isHoldAndWinCompletionReload,
    startCollection,
  ]);

  /**
   * Setup input listeners for skip functionality
   */
  useEffect(() => {
    // Spacebar handler
    const handleKeyDown = (event) => {
      if (event.code === "Space" && isCollectingRef.current) {
        event.preventDefault();
        triggerCollectionSkip();
      }
    };

    // Play button click handler
    const handlePlayButtonClick = (event) => {
      if (isCollectingRef.current) {
        event.stopPropagation(); // Prevent event bubbling
        triggerCollectionSkip();
      }
    };

    // Add event listeners
    window.addEventListener("keydown", handleKeyDown);

    const playButton = playButtonRef?.current;
    if (playButton) {
      playButton.addEventListener("click", handlePlayButtonClick, true); // Use capture phase
    }

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);

      if (playButton) {
        playButton.removeEventListener("click", handlePlayButtonClick, true);
      }
    };
  }, [triggerCollectionSkip, playButtonRef]);

  /**
   * Reset collection flags when Hold and Win reloads
   * CRITICAL: This prevents collection from being stuck after reload
   * Also sets reload state flag to block premature collection triggers
   */
  useEffect(() => {
    if (isHoldAndWinIntroReload || isHoldAndWinMidFeatureReload) {
      collectionTriggeredRef.current = false;
      isCollectingRef.current = false;
      skipTriggeredRef.current = false;
      isProcessingSkipRef.current = false;
      isInReloadStateRef.current = true; // Block collection until spin happens
    } else if (isInReloadStateRef.current) {
      // Reload flags cleared - we're out of reload state
      isInReloadStateRef.current = false;
    }
  }, [isHoldAndWinIntroReload, isHoldAndWinMidFeatureReload]);

  /**
   * Reset when Hold and Win becomes inactive
   */
  useEffect(() => {
    if (!holdAndWinActive) {
      // Clear all pending timeouts to prevent leaks
      clearAllTimeouts();
      collectionTriggeredRef.current = false;
      isCollectingRef.current = false;
      skipTriggeredRef.current = false;
      isProcessingSkipRef.current = false;
      // Show counter again when Hold and Win ends
      holdAndWinCounterRef?.current?.show();
    }
  }, [holdAndWinActive, dispatch, clearAllTimeouts, holdAndWinCounterRef]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      isCollectingRef.current = false;
      collectionTriggeredRef.current = false;
      skipTriggeredRef.current = false;
      isProcessingSkipRef.current = false;
    };
  }, [clearAllTimeouts]);

  return {
    triggerCollectionSkip, // Expose skip function for button to call
  };
};
